import React from "react";
import { AlertTriangle, RotateCcw, Home } from "lucide-react";

/**
 * Global crash guard. If any part of the React tree throws during render or
 * lifecycle (e.g. an unexpected WebGL / data error after a mobile pinch-zoom),
 * this catches it and shows a calm, branded recovery card instead of the
 * dreaded blank white screen. Data is untouched — a reload restores the view.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("CODEX ErrorBoundary caught an error:", error, info);
  }

  handleReload = () => {
    if (typeof window !== "undefined") window.location.reload();
  };

  handleHome = () => {
    if (typeof window !== "undefined") window.location.assign("/");
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        data-testid="app-error-boundary"
        className="fixed inset-0 z-[300] flex items-center justify-center bg-[#07111E] px-6"
      >
        <div className="w-full max-w-md rounded-2xl codex-glass-strong p-7 text-center">
          <div
            className="mx-auto flex h-14 w-14 items-center justify-center rounded-full"
            style={{ backgroundColor: "rgba(216,182,106,0.12)" }}
          >
            <AlertTriangle className="h-7 w-7 text-gold-light" />
          </div>
          <h2 className="mt-4 font-serif text-2xl tracking-tight text-foreground">
            Something interrupted the view
          </h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            The display hit a snag — this can happen after pinch-zooming on some
            devices. Your progress is safe. Reload to keep exploring the archive.
          </p>
          <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-center">
            <button
              data-testid="error-reload-button"
              onClick={this.handleReload}
              className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-[color:var(--codex-gold-ink)] transition-transform duration-150 active:scale-[0.98]"
              style={{ backgroundColor: "#D8B66A" }}
            >
              <RotateCcw className="h-4 w-4" /> Reload
            </button>
            <button
              data-testid="error-home-button"
              onClick={this.handleHome}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/12 px-5 py-2.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              <Home className="h-4 w-4" /> Home
            </button>
          </div>
        </div>
      </div>
    );
  }
}
